/**
 * Firebase Storage URL utilities
 *
 * Builds storage URLs dynamically from filename + context instead of storing full URLs.
 * This keeps database records smaller and paths consistent.
 */

export type StorageType = "images" | "audio" | "pdfs";

/**
 * Builds a Firebase Storage download URL from component parts.
 *
 * @param userId - User ID (Firebase Auth UID)
 * @param conversationId - Project/conversation ID
 * @param type - Storage type: "images", "audio", or "pdfs"
 * @param fileName - The sanitized filename
 * @returns Full Firebase Storage download URL
 *
 * @example
 * buildStorageUrl("user123", "project456", "images", "1734002024528_photo.jpg")
 * // Returns: "https://firebasestorage.googleapis.com/v0/b/bucket/o/users%2Fuser123%2Fprojects%2Fproject456%2Fimages%2F1734002024528_photo.jpg?alt=media"
 */
export function buildStorageUrl(
  userId: string,
  conversationId: string,
  type: StorageType,
  fileName: string
): string {
  const bucket = import.meta.env.VITE_FIREBASE_STORAGE_BUCKET;

  if (!bucket) {
    console.error("[buildStorageUrl] VITE_FIREBASE_STORAGE_BUCKET is not configured");
    return "";
  }

  const path = `users/${userId}/projects/${conversationId}/${type}/${fileName}`;
  const encodedPath = encodeURIComponent(path);

  return `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodedPath}?alt=media`;
}

/**
 * Parses a relative storage path like "audio/filename.webm" or "images/photo.jpg"
 *
 * @param relativePath - Relative path in format "{type}/{fileName}"
 * @returns Parsed type and fileName, or null if invalid
 *
 * @example
 * parseRelativeStoragePath("audio/1734002024528_audio.webm")
 * // Returns: { type: "audio", fileName: "1734002024528_audio.webm" }
 */
export function parseRelativeStoragePath(relativePath: string): {
  type: StorageType;
  fileName: string;
} | null {
  const match = relativePath.match(/^(images|audio|pdfs)\/(.+)$/);
  if (!match) return null;
  return {
    type: match[1] as StorageType,
    fileName: match[2],
  };
}
